import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FlaskConical } from 'lucide-react';
import { useTenant } from '../../auth/TenantContext';
import { AlertBanner } from '../../components/ui/AlertBanner';
import { brandKeys } from '../brand/queries';
import { fetchBrandWorkspaceFlag } from '../brand/useBrandFlags';
import { APPROVALS_LINK, approvalsLinkV2, isTestMode, scrollToApprovals } from './testMode';

/**
 * The test-mode notices: one workspace-level banner, and two that explain a post which failed
 * because of it.
 *
 * All three end with the SAME instruction, written once in {@link EnableSteps}. They are the only
 * place the app tells someone how to turn live scheduling on, so three near-copies drifting apart
 * is the failure mode worth designing out.
 */

/** The active workspace's name, or null when it is not in test mode (nothing to warn about). */
function useTestModeWorkspaceName(): string | null {
  const { activeTenant } = useTenant();
  return isTestMode(activeTenant) ? (activeTenant?.name ?? 'This') : null;
}

/**
 * Where the Approvals section is: on today's Edit page, or with Brand Workspace v2 on the active
 * workspace's Workflow page (web.md §2.6).
 *
 * The flag is the one WorkspaceGate reads for the whole session (useBrandWorkspaceV2). This only looks
 * at it and never reads it itself (`enabled: false`), so a banner adds no request of its own. Until the
 * flag is known the link is today's, which the v2 Edit route sends on to the Workflow page.
 */
function useApprovalsLink(): string {
  const { activeTenant } = useTenant();
  const flag = useQuery({
    queryKey: brandKeys.flag,
    queryFn: ({ signal }) => fetchBrandWorkspaceFlag(signal),
    enabled: false,
    staleTime: Infinity,
    retry: false,
  });
  return flag.data?.enabled === true && activeTenant ? approvalsLinkV2(activeTenant.slug) : APPROVALS_LINK;
}

/**
 * "Edit Brand Workspace", linking to the Approvals section.
 *
 * The click handler covers the case the hash alone cannot: already ON that page, where the hash is
 * unchanged so react-router fires no navigation and the page's scroll effect never re-runs. From
 * another page the element doesn't exist yet, this no-ops, and the page's own effect (WorkspaceFormPage,
 * or the v2 Edit page) scrolls once the section mounts.
 */
function ApprovalsLink() {
  const to = useApprovalsLink();
  return (
    <Link
      to={to}
      onClick={() => requestAnimationFrame(scrollToApprovals)}
      className="focus-ring rounded font-medium underline underline-offset-2"
    >
      Edit Brand Workspace
    </Link>
  );
}

/** The shared closing instruction. `lead` is the sentence that runs into "go to …". */
function EnableSteps({ lead }: { lead: string }) {
  return (
    <>
      {lead} <ApprovalsLink />, scroll to the Approvals section, check the Auto-Publishing checkbox
      and click the save button to enable live scheduling.
    </>
  );
}

/**
 * The workspace-level warning, shown at the top of every page where someone might reasonably
 * believe their scheduled posts are going out. Renders nothing when auto-publishing is on.
 */
export function TestModeBanner() {
  const name = useTestModeWorkspaceName();
  if (!name) return null;
  return (
    <div className="mb-5">
      <AlertBanner tone="warning" icon={FlaskConical}>
        <span className="mb-0.5 block font-semibold">{name} workspace is in test mode</span>
        By default, Smorgas Social has disabled scheduled posting to external platforms on new
        workspaces, in order to allow you to test the workspace when you onboard.{' '}
        <EnableSteps lead="Once you are ready to start scheduling live posts, go to" />
      </AlertBanner>
    </div>
  );
}

/**
 * The Campaigns & Posts banner for posts that failed because nothing was ever going to publish
 * them. Replaces the generic "check your post settings and connections" copy, which sends someone
 * to inspect a connection that is working perfectly.
 */
export function TestModeFailedPostsBanner({ count }: { count: number }) {
  const name = useTestModeWorkspaceName();
  if (!name || count < 1) return null;
  const s = count === 1 ? '' : 's';
  return (
    <div className="mb-5">
      <AlertBanner
        tone="error"
        action={{ label: 'View Failed Posts', to: '/campaigns?view=posts&tab=failed' }}
      >
        You have {count} failed post{s}. The {name} workspace is currently in test mode.{' '}
        <EnableSteps lead="Once you are ready to start scheduling live posts, go to" /> You can then
        reopen the failed post{s} for editing and reschedule {count === 1 ? 'it' : 'them'}.
      </AlertBanner>
    </div>
  );
}

/** The reason line on a single failed post — where a real failure would show its error. */
export function TestModeFailureNote() {
  const name = useTestModeWorkspaceName();
  if (!name) return null;
  return (
    <>
      Post failed because the {name} workspace is currently in test mode.{' '}
      <EnableSteps lead="Go to" />
    </>
  );
}
